import { useContext } from 'react'
import { useSelector } from 'react-redux'

import { digObject } from '@domain-group/javascript-utils'

import { useForm, useLatestEntity, useThunkDispatch } from '@domain-group/react-hooks'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import defaultFormState, { requiredFields } from '@models/font'

import PageContext from '@contexts/pageContext'

import * as fontActions from '@redux/modules/font'

const createFont = (params) => {
  const { fontParams, dispatch, requestOptions } = params
  const { createFont: createFn } = fontActions

  return dispatch(createFn(fontParams, requestOptions))
}

const updateFont = (params) => {
  const {
    font, fontParams, dispatch, requestOptions,
  } = params
  const { updateFont: updateFn } = fontActions

  const updatedParams = mergeAndStringifyEntityObjects(font, fontParams, [])

  return dispatch(updateFn(updatedParams, requestOptions))
}

const deleteFont = (params) => {
  const { font, dispatch, requestOptions } = params
  const { deleteFont: deleteFn } = fontActions

  return dispatch(deleteFn(font, requestOptions))
}

const createOrEditFont = params => new Promise((resolve, reject) => {
  const { font, showCreateOrEditFontModal } = params

  if (showCreateOrEditFontModal){
    const payload = {
      font,
    }

    showCreateOrEditFontModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateOrEditFontModal not defined in PageContext callbacks'))
})

export function useFontForm(font = {}, options = {}){
  const { customRequiredFields = [], validateOn } = options

  const fontForm = useForm(
    defaultFormState,
    { entity: font, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [font.id, font.cache_key],
  )

  return fontForm
}

function useFont(initEntity = {}){
  const { entity: font } = useLatestEntity(initEntity, 'fonts')

  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)
  const { showCreateOrEditFontModal } = callbacks || {}

  const { creating, deleting, updating } = useSelector(reduxState => reduxState.fonts)

  const fontFileUrl = digObject(font, 'font.file', '')
  const fontFileName = digObject(font, 'font.file_name', 'font_file')

  return {
    callbacks: {
      createFont: (fontParams, requestOptions) => (
        createFont({ fontParams, dispatch, requestOptions })
      ),
      createOrEditFont: () => createOrEditFont({ font, showCreateOrEditFontModal }),
      deleteFont: requestOptions => deleteFont({ font, dispatch, requestOptions }),
      updateFont: (fontParams, requestOptions) => updateFont({
        font, fontParams, dispatch, requestOptions,
      }),
    },
    creating,
    deleting,
    font,
    fontFileName,
    fontFileUrl,
    hasFile: !!fontFileUrl,
    updating,
  }
}

export default useFont
